/**
 * VeraLok portal entry page (2026-02 UX reconciliation).
 *
 * Renders four *structurally* different login experiences on a single
 * component, keyed by `window.location.hostname`. Shared brand tokens
 * (typography, colour ramp, spacing) are inherited from the VeraLok
 * design system; the *composition, terminology, and information
 * architecture* diverge per surface so each portal reads as its own
 * product:
 *
 *   institution.veralok.com  → InstitutionEntry
 *       Two-column, customer-facing marketing hero on the left, sign-in
 *       form on the right. This is a B2B tenant workspace — the copy
 *       and stats speak to institutional operators.
 *
 *   admin.veralok.com        → AdminEntry
 *       Dark, single-column terminal-style control-plane sign-in. No
 *       marketing hero. Amber caution chrome. No self-service
 *       registration link (internal accounts are provisioned only).
 *
 *   developer.veralok.com    → DeveloperEntry
 *       Two-column, code-first hero on the left showing a live
 *       curl-style API integration snippet, sign-in form on the right.
 *       Access-lifecycle copy is technical (API credentials, scopes,
 *       webhooks) — no institutional-operator language.
 *
 *   demo.veralok.com         → DemoEntry
 *       Single-column, sandbox-forward layout. Prominent synthetic-data
 *       banner at the top so evaluators cannot mistake this for a
 *       production surface.
 *
 *   any other host           → GenericEntry
 *       Falls back to the Institution layout as the marketing default.
 *
 * Google sign-in is removed from every entry (2026-02 reconciliation).
 * The backend `/api/auth/google/callback` handler still enforces the
 * refusals (no auto-provisioning, no internal-role Google login, no
 * elevation) so the earlier security correction is preserved.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, KeyRound, Smartphone, ArrowLeft, Terminal, Code2, FlaskConical, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { VLKButton, VLKInput, VLKLabel, toast } from "@/components/vlk";
import { apiErrorText, api } from "@/lib/api";
import { IDS } from "@/constants/testIds";
import { portalsForUser } from "@/portals/portalRouting";

/**
 * Fetch the just-authenticated user profile and route to the
 * appropriate portal. If the user has legitimate access to more than
 * one portal, land them on their primary portal — a small portal
 * selector on the destination shell handles the switcher case.
 */
async function postLoginRedirect(navigate) {
  try {
    const me = await api.get("/auth/me");
    const { primary } = portalsForUser(me.data);
    navigate(primary);
  } catch {
    navigate("/dashboard");
  }
}

// ─────────────────────────────────────────────────────────────
// Shared sign-in form controller (identical logic across portals;
// the presentation wrapping is what differs).
// ─────────────────────────────────────────────────────────────
function useLoginController() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [mfaToken, setMfaToken] = useState("");
  const [mfaEmail, setMfaEmail] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await login(email, password);
      if (res && res.mfa_required) {
        setMfaToken(res.mfa_token);
        setMfaEmail(res.email || email);
        return;
      }
      toast.success("Welcome back", { description: "Authenticated successfully." });
      await postLoginRedirect(navigate);
    } catch (err) {
      const msg = apiErrorText(err);
      setError(msg === "invalid_credentials" ? "Invalid email or password." : msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await verifyMfa(mfaToken, mfaCode.trim().replace(/\s+/g, ""));
      toast.success("Multi-factor verified", { description: "Authenticated." });
      await postLoginRedirect(navigate);
    } catch (err) {
      const msg = apiErrorText(err);
      const map = {
        invalid_mfa_code: "The code was incorrect. Try again with a fresh code or a backup code.",
        invalid_or_expired_mfa_token: "This challenge expired. Please sign in again.",
        mfa_too_many_attempts: "Too many failed attempts. Wait 15 minutes and try again.",
      };
      setError(map[msg] || msg);
      if (msg === "invalid_or_expired_mfa_token") {
        setMfaToken(""); setMfaEmail(""); setMfaCode("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancelMfa = () => {
    setMfaToken(""); setMfaEmail(""); setMfaCode(""); setError("");
  };

  return {
    email, setEmail, password, setPassword,
    error, submitting,
    mfaToken, mfaEmail, mfaCode, setMfaCode,
    onSubmit, onSubmitMfa, cancelMfa,
  };
}

// Compact form field cluster — email / password / submit / (optional MFA).
function SignInFields({ ctrl, submitLabel = "Continue", registerSlot = null, testIdPrefix = "login" }) {
  const {
    email, setEmail, password, setPassword,
    error, submitting,
    mfaToken, mfaEmail, mfaCode, setMfaCode,
    onSubmit, onSubmitMfa, cancelMfa,
  } = ctrl;

  if (mfaToken) {
    return (
      <form onSubmit={onSubmitMfa} className="space-y-4" data-testid="mfa-challenge-form">
        <div className="space-y-1.5">
          <VLKLabel>Authenticator code</VLKLabel>
          <VLKInput
            data-testid="mfa-code-input"
            autoFocus
            inputMode="text"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            placeholder="123 456 or backup code"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Enter the 6-digit code for <span className="font-mono">{mfaEmail}</span>, or a backup code
            (<span className="font-mono">ab12-cd34</span>).
          </p>
        </div>
        {error && (
          <div className="text-xs text-destructive font-mono px-3 py-2 border border-destructive/30 rounded-sm bg-destructive/5">
            {error}
          </div>
        )}
        <VLKButton type="submit" variant="primary" size="lg" className="w-full" loading={submitting}
                    disabled={mfaCode.trim().length < 6}
                    data-testid="mfa-submit">
          <Smartphone className="h-4 w-4" /> Verify
        </VLKButton>
        <VLKButton type="button" variant="ghost" size="md" className="w-full" onClick={cancelMfa} data-testid="mfa-cancel">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </VLKButton>
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid={`${testIdPrefix}-form`}>
      <div className="space-y-1.5">
        <VLKLabel>Email</VLKLabel>
        <VLKInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          data-testid={IDS.loginEmail}
        />
      </div>
      <div className="space-y-1.5">
        <VLKLabel>Password</VLKLabel>
        <VLKInput
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          data-testid={IDS.loginPassword}
        />
      </div>
      {error && (
        <div className="text-xs text-destructive font-mono px-3 py-2 border border-destructive/30 rounded-sm bg-destructive/5" data-testid={`${testIdPrefix}-error`}>
          {error}
        </div>
      )}
      <VLKButton type="submit" variant="primary" size="lg" className="w-full" loading={submitting} data-testid={IDS.loginSubmit}>
        <KeyRound className="h-4 w-4" />
        {submitLabel}
      </VLKButton>
      {registerSlot}
    </form>
  );
}

// ─────────────────────────────────────────────────────────────
// InstitutionEntry — customer-facing tenant workspace (2-col).
// ─────────────────────────────────────────────────────────────
function InstitutionEntry({ ctrl }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="portal-entry-institution">
      {/* Left: institutional marketing hero */}
      <div className="hidden lg:flex relative overflow-hidden bg-primary text-primary-foreground p-12 flex-col justify-between grid-lines">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-primary-foreground text-primary flex items-center justify-center rounded-sm">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg tracking-tight leading-none">VeraLok</div>
              <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1" data-testid="login-brand-tagline">
                Institution Portal
              </div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          className="relative z-10 max-w-md"
        >
          <div className="font-display text-4xl leading-tight tracking-tight">
            Verify identities on demand.<br />
            Bound by explicit consent.<br />
            Audited on a tamper-evident chain.
          </div>
          <p className="text-sm opacity-70 mt-6 leading-relaxed">
            The VeraLok Institution Portal is where approved banks, regulators, health
            systems and government agencies run day-to-day verification, disclosure and
            case work against citizen-controlled identities.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 border-t border-primary-foreground/10 pt-6">
            <div><div className="font-display text-lg leading-tight">Verify</div><div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">QR · disclosure</div></div>
            <div><div className="font-display text-lg leading-tight">Consent</div><div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">Every trust event</div></div>
            <div><div className="font-display text-lg leading-tight">Audit</div><div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">Tamper-evident</div></div>
          </div>
        </motion.div>

        <div className="relative z-10 text-[10px] uppercase tracking-widest opacity-60 font-mono">
          © 2026 VeraLok · Compliant with WCAG 2.1 AA · OWASP Top 10
        </div>
      </div>

      {/* Right: sign-in */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 lg:hidden flex items-center gap-2">
            <div className="h-8 w-8 bg-foreground text-background flex items-center justify-center rounded-sm">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="font-display text-lg">VeraLok · Institution Portal</div>
          </div>

          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-1">
            <Building2 className="h-3.5 w-3.5" /> Institution Portal
          </div>
          <h1 className="font-display text-2xl tracking-tight" data-testid="institution-entry-heading">Sign in to your VeraLok tenant</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="login-portal-audience">
            {ctrl.mfaToken ? "Two-factor challenge in progress." : "For approved institutional operators."}
          </p>

          <div className="mt-8">
            <SignInFields
              ctrl={ctrl}
              submitLabel="Sign in to institution"
              testIdPrefix="institution-login"
              registerSlot={
                <div className="text-xs text-muted-foreground text-center pt-2" data-testid="institution-registration-note">
                  Institution accounts are provisioned during onboarding.{" "}
                  <Link to="/register" className="underline underline-offset-2 hover:text-foreground" data-testid={IDS.registerLink}>
                    Request onboarding
                  </Link>
                </div>
              }
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AdminEntry — internal control-plane (single-column, terminal).
// ─────────────────────────────────────────────────────────────
function AdminEntry({ ctrl }) {
  return (
    <div className="min-h-screen bg-[#0a0d14] text-slate-200 flex flex-col" data-testid="portal-entry-admin">
      {/* Amber caution strip */}
      <div className="border-b border-amber-500/30 bg-amber-500/[0.06]">
        <div className="max-w-3xl mx-auto px-6 py-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-amber-300/90">
          <span data-testid="admin-entry-warning">Restricted · VeraLok internal operations</span>
          <span className="hidden sm:inline">Session events are audited</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-2 mb-8">
            <div className="h-8 w-8 border border-amber-400/50 text-amber-300 flex items-center justify-center rounded-sm bg-amber-500/[0.06]">
              <Terminal className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-xl leading-none">VeraLok Admin Console</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1" data-testid="login-brand-tagline">Internal control plane</div>
            </div>
          </div>

          <div className="rounded-sm border border-slate-800 bg-black/40 backdrop-blur p-6">
            <div className="font-mono text-[11px] text-slate-500 mb-4">
              # vlk-admin-console · password + MFA required
            </div>

            <h1 className="font-display text-xl tracking-tight" data-testid="admin-entry-heading">Operator sign-in</h1>
            <p className="text-sm text-slate-400 mt-1" data-testid="login-portal-audience">
              {ctrl.mfaToken
                ? "Multi-factor challenge in progress."
                : "Password + hardware-bound multi-factor authentication required."}
            </p>

            <div className="mt-6">
              <SignInFields
                ctrl={ctrl}
                submitLabel="Authenticate"
                testIdPrefix="admin-login"
              />
            </div>

            <div className="mt-6 pt-4 border-t border-slate-800 text-[11px] font-mono text-slate-500 space-y-1" data-testid="admin-entry-provisioning-note">
              <div><span className="text-slate-400">Access:</span>    authorized VeraLok personnel only</div>
              <div><span className="text-slate-400">Roles:</span>     system · compliance · security · identity · audit · support · executive</div>
              <div><span className="text-slate-400">Recovery:</span>  escalate through your VeraLok security contact</div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="border-t border-slate-800/70 py-3 text-[10px] font-mono uppercase tracking-widest text-slate-600 text-center">
        © 2026 VeraLok Holdings · Session bound · SOC-audited
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DeveloperEntry — technical, code-first (2-col).
// ─────────────────────────────────────────────────────────────
const DEV_CURL_SAMPLE = `# Sanity-check the VeraLok API is reachable
curl -sS https://api.veralok.com/api/health
# → {"status":"healthy","db":"connected"}

# Authenticated endpoints use an institution-scoped bearer key.
# Keys are issued in the Developer Portal under Credentials after
# sign-in, prefixed vlk_live_ (LIVE) or vlk_test_ (TEST).
#   Authorization: Bearer vlk_live_...
# Available scopes: identity.read · identity.verify · identity.lock
#                   qr.create · qr.verify · audit.read
#                   webhook.receive · recovery.attest`;

function DeveloperEntry({ ctrl }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] bg-[#0b1020] text-slate-200" data-testid="portal-entry-developer">
      {/* Left: code-first hero */}
      <div className="hidden lg:flex relative overflow-hidden p-10 flex-col justify-between border-r border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-slate-800 text-violet-300 border border-violet-400/40 flex items-center justify-center rounded-sm">
            <Code2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg tracking-tight leading-none text-slate-100">VeraLok</div>
            <div className="text-[10px] uppercase tracking-widest text-violet-300/80 mt-1" data-testid="login-brand-tagline">Developer Portal</div>
          </div>
        </div>

        <div>
          <div className="font-display text-3xl leading-tight tracking-tight text-slate-100">
            Integrate VeraLok<br />
            trust verification into<br />
            your product.
          </div>
          <p className="text-sm text-slate-400 mt-4 max-w-lg leading-relaxed">
            Manage API credentials, webhooks, scopes and rate limits. Every credential is
            institution-scoped, per-environment (TEST · LIVE), and revocable from this
            portal.
          </p>

          <pre className="mt-8 rounded-sm border border-slate-800 bg-slate-950/70 p-4 overflow-x-auto text-[11px] leading-relaxed font-mono text-slate-300"
               data-testid="developer-entry-curl-sample">
{DEV_CURL_SAMPLE}
          </pre>
        </div>

        <div className="grid grid-cols-4 gap-3 border-t border-slate-800 pt-5 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
          <div><span className="text-slate-300">/qr</span><br />verification</div>
          <div><span className="text-slate-300">/consent</span><br />grant · revoke</div>
          <div><span className="text-slate-300">/audit</span><br />tamper-evident</div>
          <div><span className="text-slate-300">/developer</span><br />keys · webhooks</div>
        </div>
      </div>

      {/* Right: technical sign-in */}
      <div className="flex items-center justify-center p-6 lg:p-10">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 lg:hidden flex items-center gap-2">
            <div className="h-8 w-8 bg-slate-800 text-violet-300 border border-violet-400/40 flex items-center justify-center rounded-sm">
              <Code2 className="h-4 w-4" />
            </div>
            <div className="font-display text-lg text-slate-100">VeraLok · Developer Portal</div>
          </div>

          <div className="rounded-sm border border-slate-800 bg-slate-900/40 backdrop-blur p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300/80 mb-2 font-mono">
              <Code2 className="h-3 w-3" /> Developer sign-in
            </div>
            <h1 className="font-display text-xl tracking-tight text-slate-100" data-testid="developer-entry-heading">Sign in to manage integrations</h1>
            <p className="text-sm text-slate-400 mt-1" data-testid="login-portal-audience">
              {ctrl.mfaToken ? "Two-factor challenge in progress." : "For VeraLok API integrators. Access is provisioned per institution."}
            </p>

            <div className="mt-6">
              <SignInFields
                ctrl={ctrl}
                submitLabel="Sign in"
                testIdPrefix="developer-login"
                registerSlot={
                  <div className="text-[11px] text-slate-500 text-center pt-2 font-mono" data-testid="developer-entry-registration-note">
                    Need API access? Your institution admin issues developer credentials
                    from the Developer Portal after sign-in.
                  </div>
                }
              />
            </div>

            <div className="mt-6 pt-4 border-t border-slate-800 text-[11px] font-mono text-slate-500">
              docs: <a className="text-violet-300 hover:underline" href="/developer/docs">/developer/docs</a>
              <span className="mx-2 text-slate-700">·</span>
              status: <a className="text-violet-300 hover:underline" href="https://api.veralok.com/api/health">/api/health</a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DemoEntry — sandbox-forward, single column.
// ─────────────────────────────────────────────────────────────
function DemoEntry({ ctrl }) {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="portal-entry-demo">
      {/* Prominent sandbox banner */}
      <div className="bg-amber-100 text-amber-900 border-b border-amber-300">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-widest">
          <FlaskConical className="h-4 w-4" />
          <span data-testid="demo-entry-banner">Sandbox · Synthetic data only · Nothing here touches production</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="h-9 w-9 bg-amber-500 text-white flex items-center justify-center rounded-sm">
              <FlaskConical className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg leading-none">VeraLok Demo Portal</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1" data-testid="login-brand-tagline">Sandbox · Synthetic data</div>
            </div>
          </div>

          <div className="rounded-sm border border-border bg-card p-6">
            <h1 className="font-display text-xl tracking-tight" data-testid="demo-entry-heading">Enter the VeraLok demo</h1>
            <p className="text-sm text-muted-foreground mt-1" data-testid="login-portal-audience">
              {ctrl.mfaToken ? "Two-factor challenge in progress." : "For evaluators. Use the demo credentials issued by your VeraLok contact."}
            </p>

            <div className="mt-6">
              <SignInFields
                ctrl={ctrl}
                submitLabel="Enter demo"
                testIdPrefix="demo-login"
              />
            </div>

            <div className="mt-6 pt-4 border-t border-border text-[11px] text-muted-foreground space-y-1" data-testid="demo-entry-boundary-note">
              <div>The demo backend is confined to the VeraLok sandbox environment.</div>
              <div>All identities, institutions and consents shown here are synthetic.</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Router — pick the entry by hostname.
// ─────────────────────────────────────────────────────────────
export default function LoginPage() {
  const ctrl = useLoginController();
  const host = (typeof window !== "undefined" && window.location?.hostname || "").toLowerCase();
  if (host.startsWith("admin."))       return <AdminEntry ctrl={ctrl} />;
  if (host.startsWith("developer."))   return <DeveloperEntry ctrl={ctrl} />;
  if (host.startsWith("demo."))        return <DemoEntry ctrl={ctrl} />;
  return <InstitutionEntry ctrl={ctrl} />; // institution.* + veralok.com fallback
}
